import { createContext, useContext, useState, useCallback } from 'react';
import en from '../locales/en.json';
import fr from '../locales/fr.json';
import es from '../locales/es.json';
import ja from '../locales/ja.json';

const locales = { en, fr, es, ja };

const STORAGE_KEY = 'wffa_lang';

const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    // Priority: URL ?lang= (forces language, useful for OBS / embedded browsers
    // that don't share localStorage), then localStorage, then default 'fr'.
    // When ?lang= is present in URL, we LOCK the language for the session
    // (window flag) so in-app toggles cannot override it. We deliberately
    // DO NOT write to localStorage to avoid polluting other tabs (admin/director).
    try {
      const params = new URLSearchParams(window.location.search);
      const urlLang = params.get('lang') || params.get('locale');
      if (urlLang && locales[urlLang]) {
        try { window.__WFFA_FORCED_LANG__ = urlLang; } catch {}
        return urlLang;
      }
    } catch {}
    try { return localStorage.getItem(STORAGE_KEY) || 'fr'; }
    catch { return 'fr'; }
  });

  const setLang = useCallback((l) => {
    // If the language was forced via URL (?lang=), ignore any in-app toggle.
    try {
      if (window.__WFFA_FORCED_LANG__) return;
    } catch {}
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch {}
  }, []);

  const t = useCallback((key, params) => {
    const keys = key.split('.');
    let val = locales[lang];
    for (const k of keys) {
      if (val == null) return key;
      val = val[k];
    }
    if (typeof val !== 'string') return key;
    if (params) {
      return val.replace(/\{(\w+)\}/g, (_, p) => params[p] ?? `{${p}}`);
    }
    return val;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, availableLanguages: ['fr', 'en', 'es', 'ja'] }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
